import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FlaskConical, Plus, Search, Calendar } from 'lucide-react';

const STATUS_COLORS = {
  Concept: 'bg-slate-100 text-slate-700 border-slate-200',
  Prototype: 'bg-blue-50 text-blue-700 border-blue-200',
  Pilot: 'bg-amber-50 text-amber-700 border-amber-200',
  Released: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Shelved: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const STATUS_OPTS = ['Concept', 'Prototype', 'Pilot', 'Released', 'Shelved'];

export default function RDProjectsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', status: 'Concept', target_launch_date: '' });
  const [saving, setSaving] = useState(false);

  // iter128 — read ?status= from the URL (sidebar deep-links use this).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('status');
    setStatusFilter(s && ['Concept', 'Prototype', 'Pilot', 'Released', 'Shelved'].includes(s) ? s : 'all');
  }, [location.search]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api().get('/rd/projects');
      setProjects(r.data || []);
    } catch (e) {
      toast.error('Failed to load R&D projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = projects.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (p.name || '').toLowerCase().includes(needle) ||
           (p.description || '').toLowerCase().includes(needle) ||
           (p.tags || []).some(t => t.toLowerCase().includes(needle));
  });

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const r = await api().post('/rd/projects', form);
      toast.success('R&D project created');
      setShowCreate(false);
      setForm({ name: '', description: '', status: 'Concept', target_launch_date: '' });
      navigate(`/rd/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="rd-projects-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-slate-800">
            <FlaskConical className="w-6 h-6 text-fuchsia-600" />
            {statusFilter === 'all' ? 'R&D Projects' : `R&D · ${statusFilter}`}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {statusFilter === 'all'
              ? 'Map out prototypes, pilot runs, and new product ideas before they hit production.'
              : `Showing projects in the ${statusFilter} phase.`}
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="rd-create-project-btn" className="gap-1.5">
          <Plus className="w-4 h-4" /> New R&D Project
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="rd-search-input"
            placeholder="Search by name, description, tags…"
            className="pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="rd-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading projects…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="rd-empty-state">
          <FlaskConical className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-base">No R&D projects yet.</p>
          <p className="text-xs mt-1">Click <b>New R&D Project</b> to spin up a workspace with draft BOMs, a whiteboard, and milestones.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <Card
              key={p.id}
              onClick={() => navigate(`/rd/${p.id}`)}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow border-slate-200"
              data-testid={`rd-project-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-800 truncate">{p.name}</h3>
                <Badge className={`${STATUS_COLORS[p.status] || STATUS_COLORS.Concept} border text-[11px]`}>{p.status}</Badge>
              </div>
              {p.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{p.description}</p>}
              <div className="flex items-center justify-between mt-3 text-[11px] text-slate-500">
                <span>Owner: {p.owner_name || p.created_by_name || '—'}</span>
                {p.target_launch_date && (
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.target_launch_date}</span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="rd-create-dialog">
          <DialogHeader><DialogTitle>New R&D Project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Name <span className="text-rose-500">*</span></label>
              <Input
                data-testid="rd-create-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Next-gen widget rev 3"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Description</label>
              <Textarea
                data-testid="rd-create-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What problem are we solving?"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="rd-create-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Target launch</label>
                <Input
                  data-testid="rd-create-target-date"
                  type="date"
                  value={form.target_launch_date}
                  onChange={(e) => setForm({ ...form, target_launch_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="rd-create-submit">
              {saving ? 'Creating…' : 'Create project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
